// RITA-1571 (Epic RITA-901) — Zeilenabstand und Abstand nach Absatz (reine Funktionen ohne DOM).
//
// Beide Einstellungen gelten für das ganze Dokument einschließlich Kopf- und Fußzeile (Entscheidung Julia
// 29.10.2026). Die Bezeichnungen entsprechen Word, damit Kunden sie aus ihren Word-Mustern ablesen können.
// Word „einfach“ ist bei Arial – und damit bei der metrisch gleichen Liberation Sans – das 1,15-Fache der
// Schriftgröße (Ascender + Descender + Zeilenzwischenraum der Schrift; an einer Word-PDF eines Kundenmusters
// nachgemessen: 12 pt → 13,8 pt). Die übrigen Stufen sind wie in Word Vielfache davon.
//
// Gespeichert werden Zahlen (`schrift.zeilenhoehe`, `schrift.absatzabstandEm`), die Editor und PDF unverändert
// verwenden. Vorlagen von vor RITA-1571 haben Zeilenhöhe 1.4 und kein `absatzabstandEm` (= 0.6 em); sie sehen
// unverändert aus, bis jemand etwas anderes wählt.
import type { Schrifteinstellungen } from './types';

export interface Abstandsstufe {
  wert: number;
  bezeichnung: string;
  /** Kurze Erklärung im aufgeklappten Auswahlfeld. */
  hinweis: string;
}

/** Zeilenhöhe neuer Vorlagen (Word „einfach“). */
export const ZEILENHOEHE_EINFACH = 1.15;

/** Wählbare Zeilenabstände (Faktor wie CSS `line-height`). */
export const ZEILENABSTAENDE: readonly Abstandsstufe[] = [
  { wert: ZEILENHOEHE_EINFACH, bezeichnung: 'einfach', hinweis: 'wie Word „Einfach“' },
  { wert: 1.3225, bezeichnung: '1,15', hinweis: 'wie Word „1,15“' },
  { wert: 1.725, bezeichnung: '1,5', hinweis: 'wie Word „1,5 Zeilen“' },
  { wert: 2.3, bezeichnung: 'doppelt', hinweis: 'wie Word „Doppelt“' },
];

/** Zeilenhöhe der Vorlagen von vor RITA-1571; im Auswahlfeld „wie bisher“, solange sie gilt. */
export const BISHERIGE_ZEILENHOEHE = 1.4;
export const WIE_BISHER = 'wie bisher';
export const WIE_BISHER_HINWEIS = 'nur bei alten Vorlagen';

/** Abstand nach Absatz, wenn das Dokument keinen eigenen hat (Vorlagen von vor RITA-1571). */
export const BISHERIGER_ABSATZABSTAND_EM = 0.6;

/** Wählbare Abstände nach Absatz in em der Grundgröße des Bereichs. */
export const ABSATZABSTAENDE: readonly Abstandsstufe[] = [
  { wert: 0, bezeichnung: 'kein', hinweis: 'Enter wie in Word' },
  { wert: BISHERIGER_ABSATZABSTAND_EM, bezeichnung: 'klein', hinweis: 'so viel wie bisher' },
  { wert: 1.2, bezeichnung: 'groß', hinweis: 'etwa doppelt so viel' },
];

/** Wirksamer Abstand nach Absatz in em. */
export function absatzabstandEmVon(schrift: Schrifteinstellungen): number {
  return schrift.absatzabstandEm ?? BISHERIGER_ABSATZABSTAND_EM;
}

/** Zahl mit deutschem Komma für nicht wählbare Werte (nur per Schnittstelle möglich). */
const alsZahl = (wert: number): string => String(wert).replace('.', ',');

/** Bezeichnung des Zeilenabstands im Auswahlfeld: „wie bisher“ nur für die alte Zeilenhöhe 1.4 (Entscheidung 3). */
export function zeilenabstandBezeichnung(zeilenhoehe: number): string {
  const stufe = ZEILENABSTAENDE.find((eintrag) => eintrag.wert === zeilenhoehe);
  if (stufe) return stufe.bezeichnung;
  return zeilenhoehe === BISHERIGE_ZEILENHOEHE ? WIE_BISHER : alsZahl(zeilenhoehe);
}

/** Bezeichnung des Abstands nach Absatz; ein nicht wählbarer Wert (nur per Schnittstelle möglich) als Zahl. */
export function absatzabstandBezeichnung(schrift: Schrifteinstellungen): string {
  const em = absatzabstandEmVon(schrift);
  return ABSATZABSTAENDE.find((stufe) => stufe.wert === em)?.bezeichnung ?? `${alsZahl(em)} em`;
}

/**
 * Stil für die Seite und den Messbereich der Seitenaufteilung: Zeilenhöhe und die CSS-Variable, aus der
 * `.vertrag-absatz` und `.vertrag-liste` (vertragseditor.css) ihren Abstand nach unten lesen. In Kopf- und
 * Fußzeile gelten beide ebenso, `em` dort bezogen auf deren Schriftgröße.
 */
export function abstandsstil(schrift: Schrifteinstellungen): Record<string, string | number> {
  return {
    lineHeight: schrift.zeilenhoehe,
    '--vertrag-absatzabstand': `${absatzabstandEmVon(schrift)}em`,
  };
}
